import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { api, getToken, setToken, type AuthUser, type AuthTenant } from '@/api/client';

/**
 * 登录态与账户信息
 *
 * 余额是全局共享的（顶栏、积分页、生成前校验都要用），
 * 所以统一放这里，任何模块消费后刷新一次即可。
 */
export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(null);
  const tenant = ref<AuthTenant | null>(null);
  const balance = ref<number | null>(null);
  const businessId = ref<string | null>(localStorage.getItem('gfc_business_id'));
  const businessName = ref<string | null>(localStorage.getItem('gfc_business_name'));
  const loading = ref(false);
  const ready = ref(false);

  const isLoggedIn = computed(() => !!getToken() && !!user.value);
  const nickname = computed(() => user.value?.nickname ?? null);
  const tenantName = computed(() => tenant.value?.name ?? null);

  function setBusiness(id: string | null, name: string | null) {
    businessId.value = id;
    businessName.value = name;
    if (id) localStorage.setItem('gfc_business_id', id);
    else localStorage.removeItem('gfc_business_id');
    if (name) localStorage.setItem('gfc_business_name', name);
    else localStorage.removeItem('gfc_business_name');
  }

  /**
   * ★ 用「当前账号自己的」档案列表校正「当前项目」
   *
   * 为什么要有这个函数：原来登录/注册/启动三处各写一遍
   *   `const list = await api.listBusiness(); if (list.length) setBusiness(list[0])`
   * —— 列表为空时**什么都不做**，于是上一个账号残留的 businessId/businessName
   *   就一直挂在 localStorage 和界面上。
   *
   * 三种调用场景（务必分清，别把用户自己选的项目误清掉）：
   *   1. 登录/注册成功瞬间 —— mode='reset'：**先清空**再按新账号的列表设置。
   *      这是唯一能确定"新身份是谁"的时刻，清完立刻按新账号的列表填，
   *      中间不会有机会渲染出别人的项目名。列表为空就保持 null。
   *   2. 页面刷新 bootstrap —— mode='keep-if-owned'：用户可能在「我的生意」
   *      里手动切换过项目，只要那个 id 确实在当前账号的列表里就保留；
   *      **不在列表里**（典型就是上一个账号的残留 id）才重置。
   *   3. 拉取失败 —— 什么都不碰。"拿不到列表" ≠ "没有档案"，
   *      网络抖一下就把用户选好的项目清掉是新的 bug。
   */
  async function syncBusiness(mode: 'reset' | 'keep-if-owned'): Promise<void> {
    if (mode === 'reset') {
      // 换号：无论后面列表能不能拿到，上一个账号的值必须先消失
      setBusiness(null, null);
    }
    let list: Array<{ id: string; name: string }> | null = null;
    try {
      list = await api.listBusiness();
    } catch {
      list = null;
    }
    if (!list) return;
    if (mode === 'keep-if-owned' && businessId.value) {
      const owned = list.find((b) => b.id === businessId.value);
      if (owned) {
        // 用户自己切过的项目，且确实属于当前账号 —— 原样保留（顺带纠正可能过期的名字）
        setBusiness(owned.id, owned.name);
        return;
      }
    }
    setBusiness(null, null);
    if (list.length) setBusiness(list[0]!.id, list[0]!.name);
  }

  async function bootstrap(): Promise<void> {
    if (!getToken()) {
      ready.value = true;
      return;
    }
    loading.value = true;
    try {
      const r = await api.me();
      user.value = r.user;
      tenant.value = r.tenant;
      balance.value = Number(r.balance.available);
      // 注意：这里不能再用 `if (!businessId.value)` —— 那正是"上一个账号的
      // businessId 被留到 localStorage、刷新后依然冒充当前项目"的漏口。
      // localStorage 里存的是 id，是否是**本账号**的必须问服务端。
      await syncBusiness('keep-if-owned');
    } catch {
      setToken(null);
      user.value = null;
      tenant.value = null;
    } finally {
      loading.value = false;
      ready.value = true;
    }
  }

  async function refreshBalance(): Promise<void> {
    const b = await api.balance();
    balance.value = Number(b.available);
  }

  /**
   * ★ 换号必须先清干净上一个账号的业务档案
   *
   * 真机复现（不点「退出登录」直接去 /login 换号）：
   *   账号 A（13800001234，有档案「老李现切牛肉火锅」businessId=4）登录后直接
   *   在 /login 换成新账号 B，界面自相矛盾 —— localStorage 仍是 business_id=4、
   *   侧栏「当前项目」和顶栏面包屑还是「老李现切牛肉火锅」，而 B 的
   *   「我的生意」区块写「还没有生意档案」。服务端没有越权（B 本来就没档案），
   *   纯粹是前端把上一个账号的 id 当成了自己的。
   *
   * 根因就是原来这里只有 `if (list.length) setBusiness(...)`：列表为空时不清旧值；
   * register() 更是完全不碰 businessId。现在统一走 syncBusiness('reset')：
   * 先清空 → 再按新账号自己的列表设置，列表为空就保持 null。
   */
  async function login(phone: string, password: string): Promise<void> {
    const r = await api.login({ phone, password });
    setToken(r.token);
    user.value = r.user;
    tenant.value = r.tenant;
    balance.value = Number(r.balance);
    await syncBusiness('reset');
  }

  async function register(phone: string, password: string, companyName: string): Promise<void> {
    const r = await api.register({ phone, password, companyName });
    setToken(r.token);
    user.value = r.user;
    tenant.value = r.tenant;
    balance.value = Number(r.balance);
    // 注册出来的是全新租户，理论上档案为空；但不能"理论"——照样先清后设，
    // 否则同一个浏览器里"注册第二个账号"就会把上一个账号的项目名带进来。
    await syncBusiness('reset');
  }

  function logout(): void {
    setToken(null);
    user.value = null;
    tenant.value = null;
    balance.value = null;
    setBusiness(null, null);
  }

  return {
    user,
    tenant,
    balance,
    businessId,
    businessName,
    loading,
    ready,
    isLoggedIn,
    nickname,
    tenantName,
    bootstrap,
    refreshBalance,
    login,
    register,
    logout,
    setBusiness,
  };
});
